// /src/pages/private/members/personalhub/hangar/filters/HangarFilters.jsx
// input-security: verified — fields: hangar-toolbar-search (via HubToolbarSearch)

import React, { useCallback, useMemo, useState } from 'react';
import Select from 'react-select';
import HelpInfoBubble from '../../../../../../components/tools/HelpInfoBubble';
import HubToolbarSearch from '../../../../../../components/tools/HubToolbarSearch';
import discordAvatarDefault from '../../../../../../medias/avatar_default.png';

const HANGAR_FILTERS_HELP = {
  title: 'Hangar filters & manual ships',
  summary:
    'Use the search bar and filter pills to control what appears in your hangar. Active filters combine together — an item must match every enabled category to stay visible.',
  steps: [
    'Search matches ship names, package names, item names, and related labels in one field.',
    'Ship filters toggle Standalone ships, Ship packages, and Loaners.',
    'Size filters show or hide ships by class: Vehicle, Snub, Small, Medium, Large, and Capital.',
    'Item filters control Item packages, Upgrades, standalone Items, and item types: Skins, FPS equipment, and Components.',
    'Use All / None to enable or disable every available filter at once, then turn individual pills back on as needed.',
    'If you linked several RSI handles, use the avatar tabs to switch the active handle and its hangar contents.',
    'To add a ship manually, choose a hull in the dropdown and click Validate. It is saved on the active handle as an in-game entry.',
  ],
  note: 'Disabled or greyed-out controls mean your role cannot use that filter or action, or your hangar has no items in that category. After a Star Citizen wipe, manually added ships are removed automatically from the database.',
};

const FILTER_LABELS = {
  standaloneShips: 'Standalone ships',
  shipPackages: 'Ship packages',
  loaners: 'Loaners',
};

const ITEM_TYPE_LABELS = {
  itemPackages: 'Item packages',
  upgrades: 'Upgrades',
  items: 'Items',
  skin: 'Skins',
  fps: 'FPS equipment',
  component: 'Components',
};

const SIZE_LABELS = {
  vehicle: 'Vehicles',
  snub: 'Snub',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  capital: 'Capital',
};

const SHIP_FILTER_KEYS = ['standaloneShips', 'shipPackages', 'loaners'];
const SIZE_FILTER_KEYS = ['vehicle', 'snub', 'small', 'medium', 'large', 'capital'];
const ITEM_FILTER_KEYS = ['items', 'itemPackages', 'upgrades'];
const ITEM_TYPE_FILTER_KEYS = ['skin', 'fps', 'component'];

function buildControllableFilterEntries({
  filterAvailability,
  canFilterShips,
  canFilterSizes,
  canFilterItems,
  canFilterItemTypes,
}) {
  const entries = [];

  if (canFilterShips) {
    SHIP_FILTER_KEYS.forEach((key) => entries.push({ group: 'ship', key }));
  }
  if (canFilterSizes) {
    SIZE_FILTER_KEYS.forEach((key) => entries.push({ group: 'size', key }));
  }
  if (canFilterItems) {
    ITEM_FILTER_KEYS.forEach((key) => entries.push({ group: 'item', key }));
  }
  if (canFilterItemTypes) {
    ITEM_TYPE_FILTER_KEYS.forEach((key) => entries.push({ group: 'itemType', key }));
  }

  return entries.filter(({ key }) => filterAvailability[key] !== false);
}

function isControllableFilterActive(entry, filters, sizeFilters, itemTypeFilters) {
  if (entry.group === 'ship') return !!filters[entry.key];
  if (entry.group === 'size') return !!sizeFilters[entry.key];
  return !!itemTypeFilters[entry.key];
}

function HangarViewModeToggle({ viewMode, setViewMode }) {
  return (
    <div
      className="hangar-filters__view-toggle"
      role="group"
      aria-label="Hangar display mode"
    >
      <button
        type="button"
        className={[
          'hangar-filters__bulk-toggle-btn',
          viewMode === 'card' ? 'hangar-filters__bulk-toggle-btn--active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setViewMode('card')}
        title="Card view"
        data-resource="private.navbar.personalhub.hangar.filters"
        data-action="write"
        data-description="Personal hub → Hangar → Filters → Card view"
      >
        Card
      </button>
      <button
        type="button"
        className={[
          'hangar-filters__bulk-toggle-btn',
          viewMode === 'list' ? 'hangar-filters__bulk-toggle-btn--active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setViewMode('list')}
        title="List view"
        data-resource="private.navbar.personalhub.hangar.filters"
        data-action="write"
        data-description="Personal hub → Hangar → Filters → List view"
      >
        List
      </button>
    </div>
  );
}

function HangarFilterBulkToggle({
  filters,
  setFilters,
  sizeFilters,
  setSizeFilters,
  itemTypeFilters,
  setItemTypeFilters,
  filterAvailability,
  canFilterShips,
  canFilterSizes,
  canFilterItems,
  canFilterItemTypes,
}) {
  const controllableEntries = useMemo(
    () =>
      buildControllableFilterEntries({
        filterAvailability,
        canFilterShips,
        canFilterSizes,
        canFilterItems,
        canFilterItemTypes,
      }),
    [
      filterAvailability,
      canFilterShips,
      canFilterSizes,
      canFilterItems,
      canFilterItemTypes,
    ]
  );

  const { allActive, noneActive } = useMemo(() => {
    if (!controllableEntries.length) {
      return { allActive: false, noneActive: true };
    }

    let activeCount = 0;
    controllableEntries.forEach((entry) => {
      if (isControllableFilterActive(entry, filters, sizeFilters, itemTypeFilters)) {
        activeCount += 1;
      }
    });

    return {
      allActive: activeCount === controllableEntries.length,
      noneActive: activeCount === 0,
    };
  }, [controllableEntries, filters, sizeFilters, itemTypeFilters]);

  const setAllFilters = useCallback(
    (enabled) => {
      if (canFilterShips) {
        setFilters((prev) => {
          const next = { ...prev };
          SHIP_FILTER_KEYS.forEach((key) => {
            if (filterAvailability[key] !== false) next[key] = enabled;
          });
          return next;
        });
      }

      if (canFilterSizes) {
        setSizeFilters((prev) => {
          const next = { ...prev };
          SIZE_FILTER_KEYS.forEach((key) => {
            if (filterAvailability[key] !== false) next[key] = enabled;
          });
          return next;
        });
      }

      if (canFilterItems || canFilterItemTypes) {
        setItemTypeFilters((prev) => {
          const next = { ...prev };

          if (canFilterItems) {
            ITEM_FILTER_KEYS.forEach((key) => {
              if (filterAvailability[key] !== false) next[key] = enabled;
            });
          }

          if (canFilterItemTypes) {
            ITEM_TYPE_FILTER_KEYS.forEach((key) => {
              if (filterAvailability[key] !== false) next[key] = enabled;
            });
          }

          return next;
        });
      }
    },
    [
      canFilterShips,
      canFilterSizes,
      canFilterItems,
      canFilterItemTypes,
      filterAvailability,
      setFilters,
      setSizeFilters,
      setItemTypeFilters,
    ]
  );

  const isDisabled = controllableEntries.length === 0;

  return (
    <div
      className="hangar-filters__bulk-toggle"
      role="group"
      aria-label="Toggle all hangar filters"
    >
      <button
        type="button"
        className={[
          'hangar-filters__bulk-toggle-btn',
          allActive ? 'hangar-filters__bulk-toggle-btn--active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setAllFilters(true)}
        disabled={isDisabled}
        title="Enable all available filters"
        data-resource="private.navbar.personalhub.hangar.filters"
        data-action="write"
        data-description="Personal hub → Hangar → Filters → All"
      >
        All
      </button>
      <button
        type="button"
        className={[
          'hangar-filters__bulk-toggle-btn',
          noneActive ? 'hangar-filters__bulk-toggle-btn--active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setAllFilters(false)}
        disabled={isDisabled}
        title="Disable all filters"
        data-resource="private.navbar.personalhub.hangar.filters"
        data-action="write"
        data-description="Personal hub → Hangar → Filters → None"
      >
        None
      </button>
    </div>
  );
}

function FilterPillRow({
  keys,
  labels,
  values,
  onToggle,
  disabled,
  availability = {},
  pillClass,
  resourceKey,
  descriptionPrefix,
}) {
  return (
    <div className="hangar-filters__pill-row">
      {keys.map((key) => {
        const active = values[key];
        const locked = availability[key] === false;
        const isDisabled = disabled || locked;
        return (
          <button
            key={key}
            type="button"
            className={[
              'filter-pill',
              pillClass,
              active ? 'active' : 'inactive',
              locked ? 'locked' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onToggle(key)}
            disabled={isDisabled}
            title={locked ? 'No items in this category' : undefined}
            data-resource={resourceKey}
            data-action="write"
            data-description={`${descriptionPrefix} → ${labels[key]}`}
          >
            {labels[key]}
          </button>
        );
      })}
    </div>
  );
}

function HangarHandleSwitch({
  hangars,
  activeHangarIndex,
  setActiveHangarIndex,
  applyHangarToState,
}) {
  const onAvatarError = useCallback((e) => {
    const img = e?.currentTarget;
    if (!img) return;
    img.onerror = null;
    img.src = discordAvatarDefault;
  }, []);

  if (!Array.isArray(hangars) || hangars.length <= 1) {
    return null;
  }

  return (
    <div
      className="hangar-filters__handles"
      role="tablist"
      aria-label="RSI handles"
    >
      {hangars.map((h, idx) => {
        const isActive = idx === activeHangarIndex;
        const label = h.isMaster ? `${h.handle} (master)` : h.handle;

        return (
          <button
            key={h.handleId}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={label}
            title={label}
            className={[
              'hangar-filters__handle-btn',
              isActive ? 'hangar-filters__handle-btn--active' : '',
              h.isMaster ? 'hangar-filters__handle-btn--master' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setActiveHangarIndex(idx);
              applyHangarToState(h);
            }}
          >
            <img
              className="hangar-filters__handle-avatar"
              src={h.discordAvatar || discordAvatarDefault}
              alt=""
              onError={onAvatarError}
            />
          </button>
        );
      })}
    </div>
  );
}

function HangarFiltersHelp() {
  return (
    <HelpInfoBubble
      help={HANGAR_FILTERS_HELP}
      variant="frame"
      ariaLabel="Hangar filters help"
      resourceKey="private.navbar.personalhub.hangar"
      description="Personal hub → Hangar → Filters help"
    />
  );
}

export default function HangarFilters({
  hangars,
  activeHangarIndex,
  setActiveHangarIndex,
  applyHangarToState,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  sizeFilters,
  setSizeFilters,
  itemTypeFilters,
  setItemTypeFilters,
  filterAvailability = {},
  allVehicles,
  onAddIngameShip,
  canSearch,
  canFilterShips,
  canFilterSizes,
  canFilterItems,
  canFilterItemTypes,
  canAddIngame,
  viewMode,
  setViewMode,
}) {
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const vehicleOptions = useMemo(
    () =>
      (allVehicles || []).map((v) => ({
        value: v.id,
        label: v.vehicleName,
      })),
    [allVehicles]
  );

  const handleValidate = () => {
    if (!selectedVehicle) return;
    onAddIngameShip(selectedVehicle.value);
    setSelectedVehicle(null);
  };

  return (
    <div className="hangar-filters hangar-filters--horizontal hangar-filters--with-help accounting-help-frame accounting-help-frame--has-help">
      <HangarFiltersHelp />

      <div className="hangar-filters__top">
        <div className="hangar-header">
          <div className="hangar-header-lines" aria-hidden="true">
            <span />
            <span />
          </div>
          <h2 className="hangar-header-title">Personal Hangar</h2>
        </div>

        <div className="hangar-filters__search">
          <HubToolbarSearch
            value={searchTerm}
            placeholder="Search ships, packages, items, skins..."
            onChange={setSearchTerm}
            disabled={!canSearch}
            resourceKey="private.navbar.personalhub.hangar.search"
            action="write"
            description="Personal hub → Hangar → Search"
          />
        </div>

        <HangarHandleSwitch
          hangars={hangars}
          activeHangarIndex={activeHangarIndex}
          setActiveHangarIndex={setActiveHangarIndex}
          applyHangarToState={applyHangarToState}
        />
      </div>

      <div className="hangar-filters__toolbar">
        <div className="hangar-filters__group hangar-filters__group--ships">
          <FilterPillRow
            keys={SHIP_FILTER_KEYS}
            labels={FILTER_LABELS}
            values={filters}
            onToggle={(key) => setFilters((f) => ({ ...f, [key]: !f[key] }))}
            disabled={!canFilterShips}
            availability={filterAvailability}
            pillClass="ships"
            resourceKey="private.navbar.personalhub.hangar.filters.ships"
            descriptionPrefix="Personal hub → Hangar → Filters → Ships"
          />
          <FilterPillRow
            keys={SIZE_FILTER_KEYS}
            labels={SIZE_LABELS}
            values={sizeFilters}
            onToggle={(key) => setSizeFilters((f) => ({ ...f, [key]: !f[key] }))}
            disabled={!canFilterSizes}
            availability={filterAvailability}
            pillClass="sizes"
            resourceKey="private.navbar.personalhub.hangar.filters.sizes"
            descriptionPrefix="Personal hub → Hangar → Filters → Sizes"
          />
        </div>

        <div className="hangar-filters__group hangar-filters__group--items">
          <FilterPillRow
            keys={ITEM_FILTER_KEYS}
            labels={ITEM_TYPE_LABELS}
            values={itemTypeFilters}
            onToggle={(key) => setItemTypeFilters((f) => ({ ...f, [key]: !f[key] }))}
            disabled={!canFilterItems}
            availability={filterAvailability}
            pillClass="items"
            resourceKey="private.navbar.personalhub.hangar.filters.items"
            descriptionPrefix="Personal hub → Hangar → Filters → Items"
          />
          <FilterPillRow
            keys={ITEM_TYPE_FILTER_KEYS}
            labels={ITEM_TYPE_LABELS}
            values={itemTypeFilters}
            onToggle={(key) => setItemTypeFilters((f) => ({ ...f, [key]: !f[key] }))}
            disabled={!canFilterItemTypes}
            availability={filterAvailability}
            pillClass="item-types"
            resourceKey="private.navbar.personalhub.hangar.filters.item-types"
            descriptionPrefix="Personal hub → Hangar → Filters → Item Types"
          />
        </div>

        <div className="hangar-filters__group hangar-filters__group--bulk">
          <HangarFilterBulkToggle
            filters={filters}
            setFilters={setFilters}
            sizeFilters={sizeFilters}
            setSizeFilters={setSizeFilters}
            itemTypeFilters={itemTypeFilters}
            setItemTypeFilters={setItemTypeFilters}
            filterAvailability={filterAvailability}
            canFilterShips={canFilterShips}
            canFilterSizes={canFilterSizes}
            canFilterItems={canFilterItems}
            canFilterItemTypes={canFilterItemTypes}
          />
          <HangarViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
        </div>

        <div className="hangar-filters__group hangar-filters__group--ingame ingame-select-block">
          <Select
            className="ingame-select"
            classNamePrefix="ingame-select"
            options={vehicleOptions}
            value={selectedVehicle}
            onChange={setSelectedVehicle}
            isClearable
            isSearchable
            placeholder="Add a ship manually"
            isDisabled={!canAddIngame}
            data-resource="private.navbar.personalhub.hangar.ingame.add"
            data-action="create"
            data-description="Personal hub → Hangar → Ingame → Add"
          />

          <button
            type="button"
            className="ingame-validate-btn"
            disabled={!selectedVehicle || !canAddIngame}
            onClick={handleValidate}
            data-resource="private.navbar.personalhub.hangar.ingame.add"
            data-action="create"
            data-route="/api/user/add-in-game-ship"
            data-description="Personal hub → Hangar → Ingame → Add"
          >
            Validate
          </button>
        </div>
      </div>
    </div>
  );
}
